import type * as APITypes from '@viamrobotics/web-client/app/v1/app';

import type { Role } from '$lib/permissions';

import { getRolePriority } from '../permissions';
import type {
  ListOrganizationMembersQuery,
  OrganizationMembers,
} from './index';

/**
 * Comparator function for invites.
 * Orders invites first by descending role priority of the invite's highest authorization,
 * then by descending alphabetical order of email.
 */
export const sortInvitesByRoleThenEmail = (
  a: APITypes.OrganizationInvite,
  b: APITypes.OrganizationInvite
) => {
  // Sort by role
  const aRolePriority = getRolePriority(
    a.authorizations[0]?.authorizationId as Role
  );
  const bRolePriority = getRolePriority(
    b.authorizations[0]?.authorizationId as Role
  );
  if (aRolePriority - bRolePriority !== 0) {
    return aRolePriority - bRolePriority;
  }

  // Then sort by email
  return a.email.localeCompare(b.email);
};

/**
 * Sorts a list of organization members. Does not sort in place.
 * Orders invites first by descending role priority of the member's highest authorization,
 * then by descending alphabetical order of email.
 * @param members A list of organization members
 * @param authsByUserIDMap A map from user ID to a list of the user's authorizations
 * @returns A sorted list of organization members
 */
export const sortOrgMembersByRoleThenEmail = (
  members: APITypes.OrganizationMember[],
  authsByUserIDMap: Record<string, APITypes.Authorization[]>
): APITypes.OrganizationMember[] => {
  const sortMembers = (
    a: APITypes.OrganizationMember,
    b: APITypes.OrganizationMember
  ) => {
    // Sort by role first, assume auths are already sorted by role priority
    const aAuth = authsByUserIDMap[a.userId]?.[0]?.authorizationId as
      | Role
      | undefined;
    const bAuth = authsByUserIDMap[b.userId]?.[0]?.authorizationId as
      | Role
      | undefined;

    const rolePriorityDifference =
      getRolePriority(aAuth) - getRolePriority(bAuth);
    if (rolePriorityDifference !== 0) {
      return rolePriorityDifference;
    }

    // Then sort by email
    if (a.emails[0] && b.emails[0]) {
      return a.emails[0].localeCompare(b.emails[0]);
    }
    return 0;
  };

  return [...members].sort(sortMembers);
};

/**
 * Organizes data from organization members and authorizations queries.
 * Separates current user from other members. Sorts organization members by role and then email for display.
 */
export const buildOrganizationMembers = (
  listOrganizationMembersQuery: ListOrganizationMembersQuery,
  authsByUserID: Record<string, APITypes.Authorization[]>,
  appUserID: string | undefined,
  mustIncludeCurrentUser = true
): OrganizationMembers | undefined => {
  if (!appUserID) {
    return undefined;
  }
  const members = listOrganizationMembersQuery.data?.members;
  if (!members) {
    return undefined;
  }
  const currentMember = members.find((member) => member.userId === appUserID);
  if (!currentMember && mustIncludeCurrentUser) {
    return undefined;
  }

  const otherMembers = members.filter((member) => member.userId !== appUserID);
  return {
    current: currentMember,
    others: sortOrgMembersByRoleThenEmail(otherMembers, authsByUserID),
  };
};
